import _ from 'lodash';
import {CardType, ExplorerContentTypes, PipettingParameterFieldKey, TaskType, TrayType} from '../constants/dataEnum';
import {csvContentToArray} from './csvHelper';
import {CSV_CELL_KEYS, dequote} from './protocolConversionHelper';
// eslint-disable-next-line import/no-cycle
import {getTasksInCard} from './cardAndTaskHelper';

export function getDataProtocolsAndFolders(protocolList) {
  const protocols = protocolList
    .filter((p) => p.type === ExplorerContentTypes.PROTOCOL && !!p.protocol)
    .map((p) => p.protocol);

  const folders = protocolList.filter((p) => p.type === ExplorerContentTypes.FOLDER && !!p.folder).map((p) => p.folder);

  return {protocols, folders};
}

export function getProtocolsAndFolders(protocolList) {
  const protocols = [];
  const folders = [];
  for (let i = 0; i < protocolList.length; i += 1) {
    const item = protocolList[i];
    if (item.type === ExplorerContentTypes.PROTOCOL) {
      protocols.push(item);
    }
    if (item.type === ExplorerContentTypes.FOLDER) {
      folders.push(item);
    }
  }

  return {protocols, folders};
}

/**
 * Compare protocol csv content
 * //  FIXME:
 * // Add mechanism to convert the old protocol on protocol share process to operator, so the old protocol will be converted to the new format,
 * // so this function will no longer be needed for running protocol in operator side
 * @param {string} csvContentNew csv content from opened protocol
 * @param {string} csvContentOld csv content from existing data in db
 * @returns boolean value from comparison
 */
export const isEqualProtocolsCsvContent = (csvContentNew, csvContentOld) => {
  let isEqual = false;
  try {
    isEqual = csvContentNew === csvContentOld;

    // Double check by compare converted CSV to array + ignore some keys
    if (!isEqual) {
      const csvContentArr = csvContentToArray(csvContentNew, CSV_CELL_KEYS);
      const currentCsvContentArr = csvContentToArray(csvContentOld, CSV_CELL_KEYS);

      isEqual = (() => {
        let resultEqual = true;

        // corner case: last indexes will never be missed if one has short lentgh with Math.max
        for (let i = 0; i < Math.max(csvContentArr.length, currentCsvContentArr.length); i += 1) {
          const taskFromOpenedProtocol = csvContentArr[i];
          const taskFromDB = currentCsvContentArr[i];
          const ignoredKeys = getProtocolCsvIgnoredKeys(taskFromOpenedProtocol, taskFromDB);

          if (!resultEqual) break;
          resultEqual = _.isEqualWith(_.omit(taskFromOpenedProtocol, ignoredKeys), _.omit(taskFromDB, ignoredKeys));
        }

        return resultEqual;
      })();
    }
  } catch (error) {
    console.error('error isEqualProtocolsCsvContent');
  }

  return isEqual;
};

/**
 * Only use this for compare the protocol csv using array of object comparison (isEqualProtocolsCsvContent)
 * @param {*} taskFromOpenedProtocol
 * @param {*} taskFromDB
 * @returns array of ignored protocol csv keys
 */
const getProtocolCsvIgnoredKeys = (taskFromOpenedProtocol, taskFromDB) => {
  const defaultIgnoredKeys = ['cardID', 'isSimpleTransfer'];
  const ignoredKeys = [...defaultIgnoredKeys];

  if (dequote(taskFromOpenedProtocol.type) === TaskType.AIR_GAP) {
    ignoredKeys.push('location');
  }

  const toCheckKeys = [
    PipettingParameterFieldKey.MIXING_CYCLE,
    PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE,
    PipettingParameterFieldKey.MIXING_POSITION_DISPENSE,
    PipettingParameterFieldKey.TIP_TOUCH_OFF_DELAY,
  ];

  for (let i = 0; i < toCheckKeys.length; i += 1) {
    const pipettingParam = toCheckKeys[i];
    const valueFromOpenedProtocol = dequote(taskFromOpenedProtocol[pipettingParam]);
    const valueFromDB = dequote(taskFromDB[pipettingParam]);

    // adding ignoreKey for the old protocol
    if ((valueFromOpenedProtocol === '0' && valueFromDB === '') || (!valueFromOpenedProtocol && !valueFromDB)) {
      ignoredKeys.push(pipettingParam);
      continue;
    }

    // ignore if the value from db don't have mixingPositionDispense
    // assuming mixingPositionDispense will have the default value with this checking process below
    if (pipettingParam === PipettingParameterFieldKey.MIXING_POSITION_DISPENSE && !valueFromDB) {
      const mixingPositionDispenseNew = valueFromOpenedProtocol;
      const mixingPositionAspirateNew = dequote(
        taskFromOpenedProtocol[PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE],
      );
      const mixingPositionAspirateOld = dequote(taskFromDB[PipettingParameterFieldKey.MIXING_POSITION_ASPIRATE]);

      if (
        mixingPositionDispenseNew === mixingPositionAspirateNew &&
        mixingPositionAspirateNew === mixingPositionAspirateOld
      ) {
        ignoredKeys.push(pipettingParam);
        continue;
      }
    }
  }

  return ignoredKeys;
};

const calculateGeneralCardRequiredTips = (tasks, maxAspirateVolume, countIgnoredTask = false) => {
  let requiredTips = 0;

  let currentTransferSequence = {
    tipChannel: {column: 0, row: 0},
    tipMultiplier: 0,
  };

  let currentOverAspirateSequence = {
    isAlreadyHoldingFreshTips: false,
    overAspirateVolume: 0,
    dispensedVolume: 0,
    isFulfilled: true,
  };

  tasks.forEach((task, index) => {
    const {type, volume, columnTipCount, rowTipCount, isIgnored} = task || {};
    if (isIgnored && !countIgnoredTask) return;

    const isOverAspirateSequence = !currentOverAspirateSequence.isFulfilled;
    const isTaskOverAspirate = volume > maxAspirateVolume;

    if (task.type === TaskType.PICK_TIP) {
      const newSequenceTipChannel = {column: columnTipCount, row: rowTipCount};
      const newSequenceTipMultiplier = 1;

      currentTransferSequence = {tipChannel: newSequenceTipChannel, tipMultiplier: newSequenceTipMultiplier};
    }

    if (type === TaskType.ASPIRATE && isTaskOverAspirate) {
      const isAlreadyHoldingFreshTips = tasks[index - 1]?.type === TaskType.PICK_TIP;
      const overAspirateVolume = volume;
      const dispensedVolume = 0;
      const isFulfilled = false;

      currentOverAspirateSequence = {overAspirateVolume, dispensedVolume, isFulfilled, isAlreadyHoldingFreshTips};
    }

    if (type === TaskType.DISPENSE && isOverAspirateSequence) {
      const {dispensedVolume, overAspirateVolume, isAlreadyHoldingFreshTips} = currentOverAspirateSequence;

      const addedDispensedVolume = volume + dispensedVolume;
      const hasFreshTipAlreadyPicked = isAlreadyHoldingFreshTips && dispensedVolume === 0;

      currentTransferSequence.tipMultiplier += Math.ceil(volume / maxAspirateVolume) - Number(hasFreshTipAlreadyPicked);
      currentOverAspirateSequence = {
        ...currentOverAspirateSequence,
        dispensedVolume: addedDispensedVolume,
        isFulfilled: addedDispensedVolume >= overAspirateVolume,
      };
    }

    if (type === TaskType.EJECT_TIP) {
      const {tipChannel, tipMultiplier} = currentTransferSequence;
      const totalTipChannel = (tipChannel.column || 0) * (tipChannel.row || 0);

      const isNeedToEmptyOverAspirate = !currentOverAspirateSequence.isFulfilled;
      const totalRequiredTipsWithOverAspirates =
        totalTipChannel * (tipMultiplier + Boolean(isNeedToEmptyOverAspirate) || 1);

      requiredTips += task.changeTip ? totalRequiredTipsWithOverAspirates : totalTipChannel;
    }
  });

  return requiredTips;
};

/**
 * Calculate required tips for card
 * @param {object} card
 * @param {object[]} cardTasks
 * @returns {object} card with requiredTips
 */
export const calculateRequiredTipsForCard = (card, cardTasks, maxAspirateVolumeCaculated) => {
  const {requiredTips: existingRequiredTips, isIgnored: isCardInactive, invalidTypes} = card;

  const isCardInvalid = invalidTypes?.length > 0;

  if (isCardInvalid || (isCardInactive && existingRequiredTips)) return existingRequiredTips;

  let newRequiredTips;

  switch (card.type) {
    case CardType.MIXING: {
      const pickupTask = Object.values(cardTasks)[0];
      const tipCount = (pickupTask?.columnTipCount || 0) * (pickupTask?.rowTipCount || 0);
      newRequiredTips = tipCount;
      break;
    }
    default: {
      const tasksInCard = getTasksInCard(card, cardTasks, {isReturnArray: true});
      const calculatedTips = calculateGeneralCardRequiredTips(
        tasksInCard,
        maxAspirateVolumeCaculated.value,
        isCardInactive,
      );

      newRequiredTips = calculatedTips;
      break;
    }
  }

  return newRequiredTips;
};

export const generateCardGrouping = (cardStateCards, cardStateGroups) => {
  let groups = {};
  let groupId;
  Object.values(cardStateCards).forEach((card) => {
    groupId = card.groupID !== null && card.groupID !== undefined ? `group-${card.groupID}` : `card-${card.ID}`;

    const group = groups[groupId] ?? {};

    if (card.groupID !== null && card.groupID !== undefined) {
      group.cards = group.cards ? [...group.cards, card] : [card];
      group.ID = card.groupID;
      group.name = cardStateGroups[card.groupID]?.name;
      group.isSelected = cardStateGroups[card.groupID]?.isSelected;
      group.isExpanded = cardStateGroups[card.groupID]?.isExpanded;
      groups = {...groups, [groupId]: group};
    } else {
      group.card = card;
    }

    groups = {...groups, [groupId]: group};
  });

  return Object.values(groups);
};

export const showHighCV = (volume) => (_, getState) => {
  const {configState, virtualTrayState, protocolState} = getState();
  const {recommendedAspirateHighVolume} = configState;
  const {lastOpenedProtocol = {}} = protocolState;
  const {volume: aspirateDispenseVolume} = virtualTrayState.aspirateDispenseVolumeModal;
  const {value: recommendedTipCapacity} = recommendedAspirateHighVolume;
  const _volume = volume || aspirateDispenseVolume;

  return (
    lastOpenedProtocol.trayType === TrayType.HIGH_VOLUME_CADDY &&
    Math.abs(_volume) < recommendedTipCapacity &&
    _volume !== 0 &&
    _volume.toString() !== '0.0'
  );
};

export const validateMinVolumeAllowed = (volume) => (_, getState) => {
  const {configState, protocolState} = getState();
  const {minAspirateHighVolume, minAspirateVolume} = configState;
  const {trayType} = protocolState.lastOpenedProtocol || {};
  const minVolumeFromConfig =
    trayType === TrayType.HIGH_VOLUME_CADDY ? minAspirateHighVolume.value : minAspirateVolume.value;
  const volumeLowerThanMinimum = Math.abs(volume) < minVolumeFromConfig && volume !== 0 && volume.toString() !== '0.0';

  return {
    validatedVolume: volumeLowerThanMinimum ? minVolumeFromConfig : volume,
    isVolumeLowerThanMinimum: volumeLowerThanMinimum,
    minimumVolume: minVolumeFromConfig,
  };
};
